import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WorkspaceHeader } from '../components/reports/Workspace/WorkspaceHeader';
import { EvidenceStepper } from '../components/reports/Workspace/EvidenceStepper';
import { EvidenceStepperFlow } from '../components/reports/Workspace/EvidenceStepperFlow';
import { ModuleShell } from '../components/reports/Workspace/ModuleShell';
import { ReportDraftingFlow } from './ReportReviewPageNew';
import { ReportPreviewFlow } from './ReportPreviewPage';
import { DocumentsDrawer } from '../components/reports/Drawers/DocumentsDrawer';
import { CommentsDrawer } from '../components/reports/Drawers/CommentsDrawer';
import { ActivityLogDrawer } from '../components/reports/Drawers/ActivityLogDrawer';
import { DossierPanel } from '../components/reports/Shared/DossierPanel';
import { useLanguageNavigation } from '../components/routing/LanguageWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { addMockReport, generateDefaultModules, getReportById } from '../utils/mockReportsData';
import { getInitialModuleBlocks, getReportModulesByType } from '../utils/reportConfiguratorBlocks';
import { getDossierByReportId } from '../utils/mockDossierData';
import { hasPermission } from '../utils/rbac';
import { EXTRACTION_STATUS, getDocumentsForReportType, getExtractionResultsForDocument } from '../utils/mockDocumentsData';
import { ACTIVITY_TYPES } from '../utils/mockActivityLogData';
import { isReportDraftLocalCacheEnabled } from '../utils/reportDraftRepository';
import { 
  advanceStoredStep,
  getStepIndex,
  getStoredMaxStepIndex,
  setStoredMaxStepIndex,
  STEPPER_STEPS,
} from '../utils/stepperProgress';
import { resolveLanguageCode } from '../utils/locale';
import { STORAGE_PREFIXES, getPrimaryStorageKey, readStorageValue, writeStorageValue } from '../utils/storageKeys';
import './ReportWorkspacePage.css';

const getRouteStepId = (pathname, search, reportId) => {
  if (!pathname || !reportId) {
    return null;
  }
  const segments = pathname.split('/').filter(Boolean);
  const reportsIndex = segments.indexOf('reports');
  if (reportsIndex === -1) {
    return null;
  }
  if (segments[reportsIndex + 1] !== String(reportId)) {
    return null;
  }
  const stepSegment = segments[reportsIndex + 2];
  if (stepSegment === 'review') {
    return 'assembled';
  }
  if (stepSegment === 'preview') {
    const params = new URLSearchParams(search || '');
    if (params.has('export')) {
      return 'export';
    }
    return 'preview';
  }
  return null;
};

const getModuleBlocksStorageKey = (reportId, moduleId) =>
  getPrimaryStorageKey(STORAGE_PREFIXES.reportModuleBlocks, `${reportId}:${moduleId}`);

const getBlockStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage || null;
};

const areBlocksApproved = (blocks) => {
  if (!Array.isArray(blocks)) {
    return false;
  }
  if (blocks.length === 0) {
    return true;
  }
  return blocks.every(block => block?.status === 'approved');
};

const getApprovalUserLabel = (user, fallback) => {
  if (!user) {
    return fallback;
  }
  if (user.name) {
    return user.name;
  }
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  if (fullName) {
    return fullName;
  }
  return user.email || fallback;
};

const isItalianLocale = (locale) => resolveLanguageCode(locale) === 'it';

const loadStoredModuleBlocks = (reportId, moduleId, reportType, locale) => {
  const fallbackBlocks = getInitialModuleBlocks(reportType, moduleId, locale);
  if (!reportId || !moduleId) {
    return fallbackBlocks;
  }
  const storage = getBlockStorage();
  if (!storage) {
    return fallbackBlocks;
  }
  try {
    const rawValue = readStorageValue(storage, STORAGE_PREFIXES.reportModuleBlocks, {
      suffix: `${reportId}:${moduleId}`,
    });
    if (!rawValue?.value) {
      return fallbackBlocks;
    }
    const parsed = JSON.parse(rawValue.value);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return fallbackBlocks;
    }
    const storedType = parsed[0]?.reportType;
    if (storedType && storedType !== reportType) {
      return fallbackBlocks;
    }
    const storedLocale = parsed[0]?.locale;
    const localeMismatch = storedLocale && locale && storedLocale !== locale;
    const shouldRebuild = localeMismatch || (!storedLocale && !isItalianLocale(locale));
    if (shouldRebuild) {
      return fallbackBlocks;
    }
    return parsed;
  } catch (error) {
    return fallbackBlocks;
  }
};

const persistModuleBlocks = (reportId, moduleId, blocks) => {
  if (!reportId || !moduleId) {
    return;
  }
  const storage = getBlockStorage();
  if (!storage) {
    return;
  }
  try {
    writeStorageValue(storage, STORAGE_PREFIXES.reportModuleBlocks, JSON.stringify(blocks || []), {
      suffix: `${reportId}:${moduleId}`,
    });
  } catch (error) {
    // Ignore storage errors (quota, private mode, etc.)
  }
};

export function ReportWorkspacePage() {
  const { id } = useParams();
  const location = useLocation();
  const { navigateTo, currentLang, getLocalizedPath } = useLanguageNavigation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const contentAreaRef = useRef(null);
  const draftToolbarHostRef = useRef(null);
  const draftPersistenceControlsRef = useRef(null);
  const hasUserSelectedStepRef = useRef(false);
  
  const stepOrder = STEPPER_STEPS;

  const [report, setReport] = useState(null);
  const [activeModule, setActiveModule] = useState('m0');
  const [blocksByModule, setBlocksByModule] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDocumentsDrawerOpen, setIsDocumentsDrawerOpen] = useState(false);
  const [isCommentsDrawerOpen, setIsCommentsDrawerOpen] = useState(false);
  const [isActivityLogDrawerOpen, setIsActivityLogDrawerOpen] = useState(false);
  const [activeStepperStep, setActiveStepperStep] = useState(stepOrder[0]);
  const [maxReachedStepIndex, setMaxReachedStepIndex] = useState(0);
  const [isExtractionLoading, setIsExtractionLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [comments, setComments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [dossier, setDossier] = useState(null);
  const [draftToolbarHostElement, setDraftToolbarHostElement] = useState(null);

  useEffect(() => {
    if (!id) {
      return;
    }
    hasUserSelectedStepRef.current = false;
    setActiveStepperStep(stepOrder[0]);
    setMaxReachedStepIndex(0);
    setDocuments([]);
    setReport(null);
    setBlocksByModule({});
    setLoading(true);
  }, [id, stepOrder]);

  useEffect(() => {
    hasUserSelectedStepRef.current = false;
  }, [location.pathname, location.search]);

  const getStepperRoute = (stepId) => {
    if (!id) {
      return null;
    }
    const routeByStep = {
      evidence: `/reports/${id}?module=${activeModule}`,
      assembled: `/reports/${id}/review`,
      preview: `/reports/${id}/preview`,
      export: `/reports/${id}/preview?export=1`,
    };
    return routeByStep[stepId] || null;
  };

  const navigateToStepperRoute = (stepId, options = null) => {
    const route = getStepperRoute(stepId);
    if (!route) {
      return false;
    }
    navigateTo(route, options || undefined);
    return true;
  };

  const updateDocuments = (updater) => {
    setDocuments(prevDocuments => {
      const nextDocuments = typeof updater === 'function' ? updater(prevDocuments) : updater;
      setReport(prevReport => (prevReport ? { ...prevReport, documents: nextDocuments } : prevReport));
      return nextDocuments;
    });
  };

  const reportModules = useMemo(
    () => getReportModulesByType(report?.type),
    [report?.type]
  );
  const moduleDefinitionMap = useMemo(
    () => new Map(reportModules.map(module => [module.id, module])),
    [reportModules]
  );

  const getModuleDefinition = (moduleId) => moduleDefinitionMap.get(moduleId);

  const getModuleName = (moduleId) => {
    const definition = getModuleDefinition(moduleId);
    if (!definition) {
      return moduleId;
    }
    if (definition.nameKey) {
      return t(definition.nameKey, { defaultValue: definition.fallbackName || moduleId });
    }
    return definition.fallbackName || moduleId;
  };

  const getModuleTabLabel = (moduleId) => {
    const definition = getModuleDefinition(moduleId);
    if (!definition?.number) {
      return getModuleName(moduleId);
    }
    return t('reports.workspace.moduleTabLabel', { number: definition.number });
  };

  const getModuleApprovalStats = (moduleId) => {
    const blocks = blocksByModule[moduleId] || [];
    const approvedCount = blocks.filter(block => block.status === 'approved').length;
    const totalCount = blocks.length;
    const progress = totalCount > 0 ? approvedCount / totalCount : 0;
    return { approvedCount, totalCount, progress };
  };
  const moduleIds = reportModules.map(module => module.id);
  const isActiveModuleApproved = useMemo(
    () => areBlocksApproved(blocksByModule[activeModule]),
    [blocksByModule, activeModule]
  );
  const areAllModulesApproved = useMemo(
    () => moduleIds.length > 0 && moduleIds.every(moduleId => areBlocksApproved(blocksByModule[moduleId])),
    [blocksByModule, moduleIds]
  );

  useEffect(() => {
    if (!id || hasUserSelectedStepRef.current) {
      return;
    }
    const routeStepId = getRouteStepId(location.pathname, location.search, id);
    const routeIndex = routeStepId ? getStepIndex(routeStepId) : -1;
    const requestedStepperRoute = location.state?.reportWorkspaceStepNav;
    const requestedStepperRouteIndex = requestedStepperRoute
      ? getStepIndex(requestedStepperRoute)
      : -1;
    const isInAppPreviewTransition = (routeStepId === 'preview' || routeStepId === 'export')
      && requestedStepperRoute === routeStepId;
    const persistedMaxReachedStepIndex = getStoredMaxStepIndex(id);
    const effectiveMaxReachedStepIndex = Math.max(
      maxReachedStepIndex,
      persistedMaxReachedStepIndex,
      requestedStepperRouteIndex
    );
    const evidenceIndex = getStepIndex('evidence');
    const maxWorkspaceIndex = evidenceIndex === -1 ? stepOrder.length - 1 : evidenceIndex;
    const clampedIndex = Math.min(
      Math.max(effectiveMaxReachedStepIndex, 0),
      maxWorkspaceIndex,
    );
    const shouldBlockDraftingRoute = routeStepId === 'assembled' && !areAllModulesApproved;
    const isRouteAllowed = routeIndex >= 0
      && (routeIndex <= effectiveMaxReachedStepIndex || isInAppPreviewTransition)
      && !shouldBlockDraftingRoute;
    const nextIndex = isRouteAllowed ? routeIndex : clampedIndex;
    if (routeIndex >= 0 && !isRouteAllowed) {
      const fallbackStep = stepOrder[nextIndex] || stepOrder[0];
      const fallbackRouteStep = ['assembled', 'preview', 'export'].includes(fallbackStep)
        ? fallbackStep
        : 'evidence';
      if (routeStepId !== fallbackRouteStep) {
        navigateToStepperRoute(fallbackRouteStep);
        return;
      }
    }
    const nextStep = stepOrder[nextIndex] || stepOrder[0];
    setActiveStepperStep(prevStep => (prevStep === nextStep ? prevStep : nextStep));
  }, [areAllModulesApproved, id, location.pathname, location.search, maxReachedStepIndex, stepOrder]);

  useEffect(() => {
    if (!report?.id || reportModules.length === 0) {
      return;
    }
    setBlocksByModule(prev => {
      const next = {};
      reportModules.forEach(module => {
        const existing = prev[module.id];
        const existingLocale = existing?.[0]?.locale;
        const shouldRefresh = !existing
          || (currentLang && existingLocale && existingLocale !== currentLang)
          || (!existingLocale && !isItalianLocale(currentLang));
        if (!shouldRefresh) {
          next[module.id] = existing;
          return;
        }
        next[module.id] = loadStoredModuleBlocks(report.id, module.id, report.type, currentLang);
      });
      return next;
    });
  }, [report?.id, report?.type, reportModules, currentLang]);

  useEffect(() => {
    if (moduleIds.length === 0) {
      return;
    }
    if (!moduleIds.includes(activeModule)) {
      setActiveModule(moduleIds[0]);
    }
  }, [activeModule, moduleIds]);

  const updateModuleBlocks = (moduleId, updater) => {
    if (!report?.id) {
      return;
    }
    setBlocksByModule(prev => {
      const currentBlocks = prev[moduleId] || [];
      const nextBlocks = typeof updater === 'function' ? updater(currentBlocks) : updater;
      const nextState = { ...prev, [moduleId]: nextBlocks };
      persistModuleBlocks(report.id, moduleId, nextBlocks);
      return nextState;
    });
  };
  
  const shouldShowStepperFlow = activeStepperStep === 'documents' || activeStepperStep === 'extraction';
  const shouldShowDraftingFlow = activeStepperStep === 'assembled';
  const shouldShowPreviewFlow = activeStepperStep === 'preview' || activeStepperStep === 'export';
  const isExportStep = activeStepperStep === 'export';
  const shouldShowWorkspacePanels = activeStepperStep === 'evidence';
  const activeStepIndex = getStepIndex(activeStepperStep);
  const stepperActiveIndex = activeStepIndex === -1 ? maxReachedStepIndex : activeStepIndex;
  const handleDraftToolbarHostRef = useCallback((node) => {
    draftToolbarHostRef.current = node;
    setDraftToolbarHostElement((prev) => (prev === node ? prev : node));
  }, []);

  const advanceToStep = (stepId) => {
    hasUserSelectedStepRef.current = true;
    const nextIndex = getStepIndex(stepId);
    if (nextIndex === -1) {
      return;
    }
    const storedIndex = advanceStoredStep(id, stepId);
    setMaxReachedStepIndex(prev => {
      const updatedIndex = Math.max(prev, nextIndex, storedIndex);
      setStoredMaxStepIndex(id, updatedIndex);
      return updatedIndex;
    });
    if (!navigateToStepperRoute(stepId, { state: { reportWorkspaceStepNav: stepId } })) {
      setActiveStepperStep(stepId);
    }
  };

  useEffect(() => {
    if (!id || !report || report.id !== id) {
      return;
    }
    const storedIndex = getStoredMaxStepIndex(id);
    const hasDocuments = documents.length > 0;
    const hasExtractionStarted = hasDocuments && documents.some(doc => doc.extractionStatus !== EXTRACTION_STATUS.PENDING);
    const isExtractionComplete = hasDocuments && documents.every(doc => doc.extractionStatus === EXTRACTION_STATUS.COMPLETE);
    
    let derivedIndex = 0;
    if (hasExtractionStarted) {
      derivedIndex = Math.max(derivedIndex, getStepIndex('extraction'));
    }
    if (isExtractionComplete) {
      derivedIndex = Math.max(derivedIndex, getStepIndex('evidence'));
    }
    if (areAllModulesApproved) {
      derivedIndex = Math.max(derivedIndex, getStepIndex('assembled'));
    }

    const nextIndex = Math.max(storedIndex, derivedIndex);
    setMaxReachedStepIndex(nextIndex);
    setStoredMaxStepIndex(id, nextIndex);
  }, [areAllModulesApproved, documents, id, report, stepOrder]);

  useEffect(() => {
    if (activeStepperStep !== 'evidence') {
      return;
    }
    setReport(prevReport => {
      if (!prevReport || !Array.isArray(prevReport.modules)) {
        return prevReport;
      }
      let shouldUpdate = false;
      const updatedModules = prevReport.modules.map(module => {
        let nextModule = module;
        if (module.status !== 'completed') {
          shouldUpdate = true;
          nextModule = { ...nextModule, status: 'completed' };
        }
        if (module.tabs) {
          let tabsUpdated = false;
          const updatedTabs = { ...module.tabs };
          ['evidence', 'ai', 'review'].forEach(tabKey => {
            const tab = updatedTabs[tabKey];
            if (tab && tab.status !== 'completed') {
              tabsUpdated = true;
              updatedTabs[tabKey] = { ...tab, status: 'completed' };
            }
          });
          if (tabsUpdated) {
            shouldUpdate = true;
            nextModule = { ...nextModule, tabs: updatedTabs };
          }
        }
        return nextModule;
      });
      if (!shouldUpdate) {
        return prevReport;
      }
      return { ...prevReport, modules: updatedModules };
    });
  }, [activeStepperStep]);

  useEffect(() => {
    if (!report?.id) {
      return;
    }
    addMockReport(report);
  }, [report]);

  // Load report on mount
  useEffect(() => {
    const loadReport = async () => {
      setLoading(true);
      const reportData = getReportById(id);
      
      if (!reportData) {
        toast({
          title: t('reports.workspace.toast.reportNotFound.title'),
          description: t('reports.workspace.toast.reportNotFound.description'),
          variant: 'destructive',
        });
        navigateTo('/reports', { replace: true });
        return;
      }
      
      const defaultModules = generateDefaultModules(reportData.type);
      const existingModules = Array.isArray(reportData.modules) ? reportData.modules : [];
      const normalizedModules = defaultModules.map(module => {
        const existing = existingModules.find(item => item.id === module.id);
        if (!existing) {
          return module;
        }
        return {
          ...module,
          ...existing,
          status: existing.status || module.status,
          tabs: existing.tabs || module.tabs,
        };
      });

      // Preload documents based on report type
      const documentsForType = getDocumentsForReportType(reportData.type);
      const seededDocuments = documentsForType.map((doc) => ({
        id: doc.id,
        name: doc.name,
        type: doc.type,
        size: doc.size,
        uploadedAt: doc.uploadedAt,
        uploadedBy: doc.uploadedBy,
        extractionStatus: EXTRACTION_STATUS.PENDING,
        extractionResults: null,
      }));

      setReport({ ...reportData, modules: normalizedModules, documents: seededDocuments });
      setDocuments(seededDocuments);
      
      // Load dossier data
      const dossierData = getDossierByReportId(id, reportData.type);
      setDossier(dossierData);
      
      // Set active module from URL or default to first configured module
      const moduleDefinitions = getReportModulesByType(reportData.type);
      const fallbackModuleId = moduleDefinitions[0]?.id || 'm0';
      const moduleFromUrl = new URLSearchParams(window.location.search).get('module') || fallbackModuleId;
      const resolvedModule = moduleDefinitions.some(module => module.id === moduleFromUrl)
        ? moduleFromUrl
        : fallbackModuleId;
      setActiveModule(prevModule => (prevModule === resolvedModule ? prevModule : resolvedModule));
      
      setLoading(false);
    };
    
    loadReport();
  }, [id, navigateTo, toast]);

  // Update URL when module changes
  useEffect(() => {
    if (!report || activeStepperStep !== 'evidence') {
      return;
    }
    const basePath = getLocalizedPath(`/reports/${id}`);
    window.history.replaceState(null, '', `${basePath}?module=${activeModule}`);
    scrollContentToTop({ behavior: 'auto' });
  }, [activeModule, activeStepperStep, id, report, currentLang, getLocalizedPath]);

  useEffect(() => {
    if (activeStepperStep !== 'evidence') {
      return;
    }
    scrollContentToTop({ behavior: 'auto' });
  }, [activeStepperStep]);

  useEffect(() => {
    const moduleArea = contentAreaRef.current;
    if (!moduleArea) {
      return undefined;
    }

    const layoutElement = moduleArea.closest('.report-workspace-page__layout');
    if (!layoutElement) {
      return undefined;
    }

    let frameId = null;
    const lastValues = {
      moduleAreaHeight: null,
      footerClearance: null,
    };

    const updateMeasurements = () => {
      if (frameId) {
        return;
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null;

        const moduleAreaRect = moduleArea.getBoundingClientRect();
        const moduleAreaHeight = `${moduleAreaRect.height}px`;
        if (moduleAreaHeight !== lastValues.moduleAreaHeight) {
          layoutElement.style.setProperty(
            '--report-workspace-module-area-height',
            moduleAreaHeight
          );
          lastValues.moduleAreaHeight = moduleAreaHeight;
        }

        const footer = moduleArea.querySelector('.module-shell__footer');
        if (footer) {
          const footerRect = footer.getBoundingClientRect();
          const footerStyles = window.getComputedStyle(footer);
          const marginTop = parseFloat(footerStyles.marginTop) || 0;
          const marginBottom = parseFloat(footerStyles.marginBottom) || 0;
          const clearance = `${footerRect.height + marginTop + marginBottom}px`;
          if (clearance !== lastValues.footerClearance) {
            layoutElement.style.setProperty('--report-workspace-footer-clearance', clearance);
            lastValues.footerClearance = clearance;
          }
        } else if (lastValues.footerClearance !== '0px') {
          layoutElement.style.setProperty('--report-workspace-footer-clearance', '0px');
          lastValues.footerClearance = '0px';
        }

      });
    };

    updateMeasurements();

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateMeasurements);
      resizeObserver.observe(moduleArea);
    }

    window.addEventListener('resize', updateMeasurements);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateMeasurements);
    };
  }, [activeModule, shouldShowWorkspacePanels, loading]);


  const handleSave = async () => {
    // Check permissions
    if (!hasPermission(user, 'SAVE_REPORT')) {
      toast({
        title: t('reports.workspace.toast.permissionDenied.title'),
        description: t('reports.workspace.toast.permissionDenied.saveReport'),
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    // Mock save - simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
    
    // Log activity
    const newActivity = {
      id: `activity-${Date.now()}`,
      action: 'edited',
      user: user?.email || 'unknown',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
      userRole: user?.role,
      timestamp: new Date(),
      details: {
        target: 'report',
        targetId: id,
        targetName: report?.name || t('reports.workspace.fallback.report'),
      },
      moduleId: null,
    };
    setActivities(prev => [newActivity, ...prev]);
    
    toast({
      title: t('reports.workspace.toast.reportSaved.title'),
      description: t('reports.workspace.toast.reportSaved.description'),
    });
  };

  const handleLockToggle = async () => {
    const isLocking = !report.locked;
    
    // Check permissions
    if (isLocking && !hasPermission(user, 'LOCK_REPORT')) {
      toast({
        title: t('reports.workspace.toast.permissionDenied.title'),
        description: t('reports.workspace.toast.permissionDenied.lockReport'),
        variant: 'destructive',
      });
      return;
    }
    
    if (!isLocking && !hasPermission(user, 'UNLOCK_REPORT')) {
      toast({
        title: t('reports.workspace.toast.permissionDenied.title'),
        description: t('reports.workspace.toast.permissionDenied.unlockReport'),
        variant: 'destructive',
      });
      return;
    }
    
    if (report.locked) {
      // Unlock
      setReport(prev => ({ ...prev, locked: false }));
    } else {
      // Lock
      setReport(prev => ({ ...prev, locked: true }));
    }
    
    // Log activity
    const newActivity = {
      id: `activity-${Date.now()}`,
      action: isLocking ? 'locked' : 'unlocked',
      user: user?.email || 'unknown',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
      userRole: user?.role,
      timestamp: new Date(),
      details: {
        target: 'report',
        targetId: id,
        targetName: report?.name || t('reports.workspace.fallback.report'),
      },
      moduleId: null,
    };
    setActivities(prev => [newActivity, ...prev]);
    
    toast({
      title: isLocking
        ? t('reports.workspace.toast.reportLocked.title')
        : t('reports.workspace.toast.reportUnlocked.title'),
      description: isLocking
        ? t('reports.workspace.toast.reportLocked.description')
        : t('reports.workspace.toast.reportUnlocked.description'),
    });
  };

  const handleStepClick = (stepId) => {
    const nextIndex = getStepIndex(stepId);
    if (nextIndex === -1) {
      return;
    }
    if (stepId === 'assembled' && !areAllModulesApproved) {
      toast({
        title: t('reports.workspace.toast.approvalGate.drafting.title'),
        description: t('reports.workspace.toast.approvalGate.drafting.description'),
      });
      return;
    }
    if (nextIndex <= maxReachedStepIndex) {
      hasUserSelectedStepRef.current = true;
      if (!navigateToStepperRoute(stepId, { state: { reportWorkspaceStepNav: stepId } })) {
        setActiveStepperStep(stepId);
      }
    }
  };

  const handleDocumentsUpload = (payload) => {
    if (Array.isArray(payload)) {
      const newDocuments = payload.map(file => ({
        id: `doc-${Date.now()}-${Math.random()}`,
        name: file.name,
        type: file.type || file.name.split('.').pop().toLowerCase(),
        size: file.size,
        uploadedAt: new Date(),
        uploadedBy: user?.email || t('reports.workspace.fallback.user'),
        extractionStatus: EXTRACTION_STATUS.PENDING,
        extractionResults: null,
      }));
      updateDocuments(prev => [...prev, ...newDocuments]);
      return;
    }

    if (payload && payload.id) {
      updateDocuments(prev => [...prev, payload]);
    }
  };

  const scrollContentToTop = ({ behavior = 'smooth' } = {}) => {
    if (contentAreaRef.current) {
      contentAreaRef.current.scrollTo({ top: 0, left: 0, behavior });
    }
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior });
    }
  };

  const flushDraftLocalBeforeStepChange = useCallback(async () => {
    const controls = draftPersistenceControlsRef.current;
    const flushLocalNow = controls?.flushLocalNow;
    const flushRemoteNow = controls?.flushRemoteNow;
    if (typeof flushLocalNow !== 'function') {
      return;
    }
    try {
      await Promise.race([
        Promise.resolve().then(async () => {
          await flushLocalNow({ reason: 'workspace-step-change', skipRemoteSchedule: true });
          if (!isReportDraftLocalCacheEnabled() && typeof flushRemoteNow === 'function') {
            await flushRemoteNow();
          }
        }),
        new Promise((resolve) => setTimeout(resolve, 1000)),
      ]);
    } catch (error) {
      // Best effort only.
    }
  }, []);

  const handleContinueToExtraction = async () => {
    advanceToStep('extraction');
    scrollContentToTop();
    setIsExtractionLoading(true);
    updateDocuments(prev =>
      prev.map(doc => ({
        ...doc,
        extractionStatus: EXTRACTION_STATUS.PROCESSING,
      }))
    );

    await new Promise(resolve => setTimeout(resolve, 1600));

    updateDocuments(prev =>
      prev.map(doc => ({
        ...doc,
        extractionStatus: EXTRACTION_STATUS.COMPLETE,
        extractionResults: getExtractionResultsForDocument(doc),
      }))
    );
    setIsExtractionLoading(false);
  };

  const handleContinueToEvidence = () => {
    advanceToStep('evidence');
    scrollContentToTop();
  };

  const handleBackToDocuments = () => {
    hasUserSelectedStepRef.current = true;
    setActiveStepperStep('documents');
    scrollContentToTop();
  };

  const handleBackToExtraction = () => {
    hasUserSelectedStepRef.current = true;
    setActiveStepperStep('extraction');
    scrollContentToTop();
  };

  const handleContinueToAssembled = () => {
    if (!areAllModulesApproved) {
      toast({
        title: t('reports.workspace.toast.approvalGate.drafting.title'),
        description: t('reports.workspace.toast.approvalGate.drafting.description'),
      });
      return;
    }
    advanceToStep('assembled');
    scrollContentToTop();
  };

  const handleContinueToPreview = async () => {
    await flushDraftLocalBeforeStepChange();
    advanceToStep('preview');
    scrollContentToTop();
  };

  const handleDraftAutosaveActivity = () => {
    const newActivity = {
      id: `activity-${Date.now()}`,
      action: ACTIVITY_TYPES.AUTOSAVED,
      user: user?.email || 'unknown',
      userName: user ? `${user.firstName} ${user.lastName}` : t('reports.workspace.fallback.unknown'),
      userRole: user?.role,
      timestamp: new Date(),
      details: {
        target: 'report',
        targetId: id,
        targetName: report?.name || t('reports.workspace.fallback.report'),
      },
      moduleId: null,
    };
    setActivities(prev => [newActivity, ...prev]);
  };

  const handleOpenExport = () => {
    advanceToStep('export');
    scrollContentToTop();
  };

  const handleCloseExport = () => {
    advanceToStep('preview');
    scrollContentToTop();
  };

  const handleExportComplete = (updatedReport) => {
    if (!updatedReport) {
      return;
    }
    setReport(updatedReport);
    if (id) {
      const exportIndex = advanceStoredStep(id, 'export');
      setMaxReachedStepIndex(prev => Math.max(prev, exportIndex));
      setActiveStepperStep('export');
    }
    navigateTo('/reports');
  };

  const handleBackToDrafting = () => {
    advanceToStep('assembled');
    scrollContentToTop();
  };

  const handleBackFromDrafting = async () => {
    await flushDraftLocalBeforeStepChange();
    hasUserSelectedStepRef.current = true;
    setActiveStepperStep('evidence');
    scrollContentToTop();
  };

  const handleModuleClick = (moduleId) => {
    setActiveModule(moduleId);
    scrollContentToTop();
  };

  const handlePreviousModule = () => {
    const currentIndex = moduleIds.indexOf(activeModule);
    if (currentIndex > 0) {
      setActiveModule(moduleIds[currentIndex - 1]);
      scrollContentToTop();
    }
  };

  const handleNextModule = () => {
    if (!isActiveModuleApproved) {
      toast({
        title: t('reports.workspace.toast.approvalGate.nextModule.title'),
        description: t('reports.workspace.toast.approvalGate.nextModule.description'),
      });
      return;
    }
    const currentIndex = moduleIds.indexOf(activeModule);
    if (currentIndex < moduleIds.length - 1) {
      setActiveModule(moduleIds[currentIndex + 1]);
      scrollContentToTop();
    }
  };

  const handleSaveModule = async (moduleId) => {
    // Check permissions
    if (!hasPermission(user, 'SAVE_REPORT')) {
      toast({
        title: t('reports.workspace.toast.permissionDenied.title'),
        description: t('reports.workspace.toast.permissionDenied.saveModule'),
        variant: 'destructive',
      });
      return;
    }

    // Mock save module
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Log activity
    const newActivity = {
      id: `activity-${Date.now()}`,
      action: 'edited',
      user: user?.email || 'unknown',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
      userRole: user?.role,
      timestamp: new Date(),
      details: {
        target: 'module',
        targetId: moduleId,
        targetName: getModuleName(moduleId),
      },
      moduleId: moduleId,
    };
    setActivities(prev => [newActivity, ...prev]);
    
    toast({
      title: t('reports.workspace.toast.moduleSaved.title'),
      description: t('reports.workspace.toast.moduleSaved.description'),
    });
  };

  const handleLockModule = async (moduleId) => {
    // Check permissions
    if (!hasPermission(user, 'LOCK_REPORT')) {
      toast({
        title: t('reports.workspace.toast.permissionDenied.title'),
        description: t('reports.workspace.toast.permissionDenied.lockUnlockModule'),
        variant: 'destructive',
      });
      return;
    }
    
    // Mock lock/unlock module
    const module = report?.modules?.find(m => m.id === moduleId);
    const isLocking = module?.status !== 'locked';
    
    setReport(prev => {
      const updatedModules = prev.modules.map(m => {
        if (m.id === moduleId) {
          return {
            ...m,
            status: m.status === 'locked' ? 'needs_review' : 'locked',
            lockedBy: m.status === 'locked' ? null : user?.firstName + ' ' + user?.lastName,
          };
        }
        return m;
      });
      return { ...prev, modules: updatedModules };
    });
    
    // Log activity
    const newActivity = {
      id: `activity-${Date.now()}`,
      action: isLocking ? 'locked' : 'unlocked',
      user: user?.email || 'unknown',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
      userRole: user?.role,
      timestamp: new Date(),
      details: {
        target: 'module',
        targetId: moduleId,
        targetName: getModuleName(moduleId),
      },
      moduleId: moduleId,
    };
    setActivities(prev => [newActivity, ...prev]);
    
    toast({
      title: isLocking
        ? t('reports.workspace.toast.moduleLocked.title')
        : t('reports.workspace.toast.moduleUnlocked.title'),
      description: isLocking 
        ? t('reports.workspace.toast.moduleLocked.description')
        : t('reports.workspace.toast.moduleUnlocked.description'),
    });
  };

  const handleCompleteModule = async (moduleId) => {
    if (!hasPermission(user, 'SAVE_REPORT')) {
      toast({
        title: t('reports.workspace.toast.permissionDenied.title'),
        description: t('reports.workspace.toast.permissionDenied.saveModule'),
        variant: 'destructive',
      });
      return;
    }

    const targetModule = report?.modules?.find(m => m.id === moduleId);
    if (!targetModule || targetModule.status === 'locked' || targetModule.status === 'completed') {
      return;
    }

    setReport(prev => {
      if (!prev) {
        return prev;
      }
      const updatedModules = prev.modules.map(m => {
        if (m.id === moduleId) {
          return { ...m, status: 'completed' };
        }
        return m;
      });
      return { ...prev, modules: updatedModules };
    });

    const newActivity = {
      id: `activity-${Date.now()}`,
      action: ACTIVITY_TYPES.MODULE_COMPLETED,
      user: user?.email || 'unknown',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
      userRole: user?.role,
      timestamp: new Date(),
      details: {
        target: 'module',
        targetId: moduleId,
        targetName: getModuleName(moduleId),
      },
      moduleId: moduleId,
    };
    setActivities(prev => [newActivity, ...prev]);

    toast({
      title: t('reports.workspace.toast.statusUpdated.title'),
      description: t('reports.workspace.toast.statusUpdated.description', {
        status: t('reports.status.completed', { defaultValue: 'completed' }),
      }),
    });
  };

  const handleCompleteAllModules = async () => {
    if (!hasPermission(user, 'SAVE_REPORT')) {
      toast({
        title: t('reports.workspace.toast.permissionDenied.title'),
        description: t('reports.workspace.toast.permissionDenied.saveModule'),
        variant: 'destructive',
      });
      return;
    }

    const modulesToUpdate = report?.modules?.filter(
      module => module.status !== 'completed' && module.status !== 'locked'
    );

    if (!modulesToUpdate || modulesToUpdate.length === 0) {
      return;
    }

    setReport(prev => {
      if (!prev) {
        return prev;
      }
      const updatedModules = prev.modules.map(module => {
        if (module.status === 'locked') {
          return module;
        }
        return { ...module, status: 'completed' };
      });
      return { ...prev, modules: updatedModules };
    });

    const completedActivities = modulesToUpdate.map(module => ({
      id: `activity-${Date.now()}-${module.id}`,
      action: ACTIVITY_TYPES.MODULE_COMPLETED,
      user: user?.email || 'unknown',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
      userRole: user?.role,
      timestamp: new Date(),
      details: {
        target: 'module',
        targetId: module.id,
        targetName: getModuleName(module.id),
      },
      moduleId: module.id,
    }));
    setActivities(prev => [...completedActivities, ...prev]);

    toast({
      title: t('reports.workspace.toast.allModulesCompleted.title'),
      description: t('reports.workspace.toast.allModulesCompleted.description'),
    });
  };

  const handleApproveAllBlocks = (moduleId) => {
    if (!hasPermission(user, 'SAVE_REPORT')) {
      toast({
        title: t('reports.workspace.toast.permissionDenied.title'),
        description: t('reports.workspace.toast.permissionDenied.saveModule'),
        variant: 'destructive',
      });
      return;
    }

    updateModuleBlocks(moduleId, (prevBlocks) => {
      if (!Array.isArray(prevBlocks) || prevBlocks.length === 0) {
        return prevBlocks;
      }
      const userLabel = getApprovalUserLabel(user, t('reports.workspace.fallback.user'));
      const approvedAt = new Date().toISOString();
      return prevBlocks.map(block => {
        if (block?.status === 'approved') {
          return block;
        }
        return {
          ...block,
          status: 'approved',
          approvedBy: userLabel,
          approvedAt: approvedAt,
        };
      });
    });
  };

  const handleStatusTransition = async (newStatus) => {
    // Check permissions
    if (!hasPermission(user, 'REQUEST_REVIEW')) {
      toast({
        title: t('reports.workspace.toast.permissionDenied.title'),
        description: t('reports.workspace.toast.permissionDenied.changeStatus'),
        variant: 'destructive',
      });
      return;
    }
    
    // Validate transition
    const currentModule = report?.modules?.find(m => m.id === activeModule);
    const currentStatus = currentModule?.status || 'draft';
    const validTransitions = {
      draft: ['needs_review'],
      needs_review: ['draft', 'locked'],
      locked: ['needs_review'],
    };
    
    if (!validTransitions[currentStatus]?.includes(newStatus)) {
      toast({
        title: t('reports.workspace.toast.invalidTransition.title'),
        description: t('reports.workspace.toast.invalidTransition.description', {
          from: t(`reports.status.${currentStatus}`, { defaultValue: currentStatus }),
          to: t(`reports.status.${newStatus}`, { defaultValue: newStatus }),
        }),
        variant: 'destructive',
      });
      return;
    }
    
    // Additional permission check for lock/unlock
    if (newStatus === 'locked' && !hasPermission(user, 'LOCK_REPORT')) {
      toast({
        title: t('reports.workspace.toast.permissionDenied.title'),
        description: t('reports.workspace.toast.permissionDenied.lockModule'),
        variant: 'destructive',
      });
      return;
    }
    
    if (currentStatus === 'locked' && !hasPermission(user, 'UNLOCK_REPORT')) {
      toast({
        title: t('reports.workspace.toast.permissionDenied.title'),
        description: t('reports.workspace.toast.permissionDenied.unlockModule'),
        variant: 'destructive',
      });
      return;
    }
    
    // Update module status
    setReport(prev => {
      const updatedModules = prev.modules.map(m => {
        if (m.id === activeModule) {
          return { ...m, status: newStatus };
        }
        return m;
      });
      return { ...prev, modules: updatedModules };
    });
    
    // Log activity with proper details
    const newActivity = {
      id: `activity-${Date.now()}`,
      action: 'status_changed',
      user: user?.email || 'unknown',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
      userRole: user?.role,
      timestamp: new Date(),
      details: {
        target: 'module',
        targetId: activeModule,
        targetName: getModuleName(activeModule),
        oldStatus: currentStatus,
        newStatus: newStatus,
      },
      moduleId: activeModule,
    };
    setActivities(prev => [newActivity, ...prev]);
    
    toast({
      title: t('reports.workspace.toast.statusUpdated.title'),
      description: t('reports.workspace.toast.statusUpdated.description', {
        status: t(`reports.status.${newStatus}`, { defaultValue: newStatus }),
      }),
    });
  };

  const handleAddComment = (text, moduleId) => {
    // Check permissions
    if (!hasPermission(user, 'ADD_COMMENT')) {
      toast({
        title: t('reports.workspace.toast.permissionDenied.title'),
        description: t('reports.workspace.toast.permissionDenied.addComment'),
        variant: 'destructive',
      });
      return;
    }

    const newComment = {
      id: `comment-${Date.now()}`,
      moduleId: moduleId,
      author: {
        id: user?.id,
        name: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
        email: user?.email,
        role: user?.role,
      },
      text: text,
      timestamp: new Date(),
      createdAt: new Date(),
      replies: [],
      resolved: false,
    };
    setComments(prev => [...prev, newComment]);
    
    // Log activity
    const newActivity = {
      id: `activity-${Date.now()}`,
      action: 'comment_added',
      user: user?.email || 'unknown',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
      userRole: user?.role,
      timestamp: new Date(),
      details: {
        target: 'module',
        targetId: moduleId,
        targetName: getModuleName(moduleId),
      },
      moduleId: moduleId,
    };
    setActivities(prev => [newActivity, ...prev]);
    
    toast({
      title: t('reports.workspace.toast.commentAdded.title'),
      description: t('reports.workspace.toast.commentAdded.description'),
    });
  };

  const handleReply = (commentId, text) => {
    // Find the comment and add reply
    const addReplyToComment = (comments, targetId, replyText) => {
      return comments.map(comment => {
        if (comment.id === targetId) {
          const newReply = {
            id: `reply-${Date.now()}-${Math.random()}`,
            moduleId: comment.moduleId,
            author: {
              id: user?.id,
              name: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
              email: user?.email,
              role: user?.role,
            },
            text: replyText,
            timestamp: new Date(),
            replies: [],
            resolved: false,
          };
          return {
            ...comment,
            replies: [...(comment.replies || []), newReply],
          };
        }
        // Recursively check replies
        if (comment.replies && comment.replies.length > 0) {
          return {
            ...comment,
            replies: addReplyToComment(comment.replies, targetId, replyText),
          };
        }
        return comment;
      });
    };

    setComments(prev => addReplyToComment(prev, commentId, text));
    
    // Log activity
    const newActivity = {
      id: `activity-${Date.now()}`,
      action: 'comment_added',
      user: user?.email || 'unknown',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
      userRole: user?.role,
      timestamp: new Date(),
      details: {
        target: 'comment',
        targetId: commentId,
        targetName: commentId,
      },
      moduleId: null,
    };
    setActivities(prev => [newActivity, ...prev]);
    
    toast({
      title: t('reports.workspace.toast.replyAdded.title'),
      description: t('reports.workspace.toast.replyAdded.description'),
    });
  };

  const handleEditComment = (commentId, newText) => {
    // Find and update comment (including nested replies)
    const updateComment = (comments, targetId, newText) => {
      return comments.map(comment => {
        if (comment.id === targetId) {
          return { ...comment, text: newText };
        }
        // Recursively check replies
        if (comment.replies && comment.replies.length > 0) {
          return {
            ...comment,
            replies: updateComment(comment.replies, targetId, newText),
          };
        }
        return comment;
      });
    };

    setComments(prev => updateComment(prev, commentId, newText));
    
    // Log activity
    const newActivity = {
      id: `activity-${Date.now()}`,
      action: 'edited',
      user: user?.email || 'unknown',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
      userRole: user?.role,
      timestamp: new Date(),
      details: {
        target: 'comment',
        targetId: commentId,
        targetName: commentId,
      },
      moduleId: null,
    };
    setActivities(prev => [newActivity, ...prev]);
    
    toast({
      title: t('reports.workspace.toast.commentUpdated.title'),
      description: t('reports.workspace.toast.commentUpdated.description'),
    });
  };

  const handleDeleteComment = (commentId) => {
    // Find and delete comment (including nested replies)
    const deleteComment = (comments, targetId) => {
      return comments.filter(comment => {
        if (comment.id === targetId) {
          return false;
        }
        // Recursively filter replies
        if (comment.replies && comment.replies.length > 0) {
          comment.replies = deleteComment(comment.replies, targetId);
        }
        return true;
      });
    };

    setComments(prev => deleteComment(prev, commentId));
    
    // Log activity
    const newActivity = {
      id: `activity-${Date.now()}`,
      action: 'edited',
      user: user?.email || 'unknown',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
      userRole: user?.role,
      timestamp: new Date(),
      details: {
        target: 'comment',
        targetId: commentId,
        targetName: commentId,
      },
      moduleId: null,
    };
    setActivities(prev => [newActivity, ...prev]);
    
    toast({
      title: t('reports.workspace.toast.commentDeleted.title'),
      description: t('reports.workspace.toast.commentDeleted.description'),
    });
  };

  const handleResolveComment = (commentId) => {
    // Find and resolve comment (including nested replies)
    const resolveComment = (comments, targetId) => {
      return comments.map(comment => {
        if (comment.id === targetId) {
          return {
            ...comment,
            resolved: true,
            resolvedBy: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
            resolvedAt: new Date(),
          };
        }
        // Recursively check replies
        if (comment.replies && comment.replies.length > 0) {
          return {
            ...comment,
            replies: resolveComment(comment.replies, targetId),
          };
        }
        return comment;
      });
    };

    setComments(prev => resolveComment(prev, commentId));
    
    // Log activity
    const newActivity = {
      id: `activity-${Date.now()}`,
      action: 'edited',
      user: user?.email || 'unknown',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
      userRole: user?.role,
      timestamp: new Date(),
      details: {
        target: 'comment',
        targetId: commentId,
        targetName: commentId,
      },
      moduleId: null,
    };
    setActivities(prev => [newActivity, ...prev]);
    
    toast({
      title: t('reports.workspace.toast.commentResolved.title'),
      description: t('reports.workspace.toast.commentResolved.description'),
    });
  };

  if (loading) {
    return (
      <div className="report-workspace-page">
        <div className="report-workspace-page__loading">
          <p>{t('reports.workspace.loading')}</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="report-workspace-page">
        <div className="report-workspace-page__error">
          <p>{t('reports.workspace.notFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="report-workspace-page">
      <div className={`report-workspace-page__layout${shouldShowDraftingFlow ? ' report-workspace-page__layout--drafting' : ''}`}>
        <div className="report-workspace-page__header-wrapper">
          <WorkspaceHeader
            report={report}
            onSave={handleSave}
            onLockToggle={handleLockToggle}
            saving={saving}
          />
        </div>

        <div className="report-workspace-page__stepper">
          <EvidenceStepper
            onStepClick={handleStepClick}
            maxReachedStepIndex={maxReachedStepIndex}
            activeStepIndex={stepperActiveIndex}
          />
        </div>

        {shouldShowDraftingFlow ? (
          <div className="report-workspace-page__drafting-toolbar-host">
            <div
              className="report-workspace-page__drafting-toolbar-host-inner"
              ref={handleDraftToolbarHostRef}
            />
          </div>
        ) : null}

        {shouldShowWorkspacePanels && (
          <div className="report-workspace-page__tabs-bar">
            <div className="report-workspace-page__tabs analytics-header-section__tabs">
              {reportModules.map((module) => {
                const isActive = activeModule === module.id;
                const { approvedCount, totalCount, progress } = getModuleApprovalStats(module.id);
                const progressPercent = Math.round(progress * 100);
                const isComplete = totalCount > 0 && approvedCount === totalCount;
                return (
                  <button
                    key={module.id}
                    type="button"
                    className={`analytics-tab ${isActive ? 'analytics-tab--active' : ''}`}
                    onClick={() => handleModuleClick(module.id)}
                  >
                    <span className="report-workspace-page__tab-content">
                      <span className="report-workspace-page__tab-label">
                        {getModuleTabLabel(module.id)}
                      </span>
                      <span className="report-workspace-page__tab-status">
                        <span className="report-workspace-page__tab-approval">
                          {t('reports.workspace.blocks.approvalCount', {
                            approved: approvedCount,
                            total: totalCount,
                          })}
                        </span>
                        {totalCount > 0 && (
                          isComplete ? (
                            <span className="report-workspace-page__tab-complete">
                              {t('reports.workspace.blocks.complete', { defaultValue: 'Complete' })}
                            </span>
                          ) : (
                            <span
                              className="report-workspace-page__tab-progress"
                              role="progressbar"
                              aria-label={t('reports.workspace.blocks.approvalCount', {
                                approved: approvedCount,
                                total: totalCount,
                              })}
                              aria-valuemin={0}
                              aria-valuemax={totalCount}
                              aria-valuenow={approvedCount}
                            >
                              <span
                                className="report-workspace-page__tab-progress-bar"
                                style={{ width: `${progressPercent}%` }}
                              />
                            </span>
                          )
                        )}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="report-workspace-page__main">
          <div className="report-workspace-page__content">
            <div 
              className={`report-workspace-page__module-area${
                shouldShowDraftingFlow ? ' report-workspace-page__module-area--drafting' : ''
              }`}
              ref={contentAreaRef}
            >
              {shouldShowStepperFlow ? (
                <EvidenceStepperFlow
                  step={activeStepperStep}
                  documents={documents}
                  reportType={report?.type}
                  currentUser={user}
                  onUpload={handleDocumentsUpload}
                  onContinue={handleContinueToExtraction}
                  onBackToDocuments={handleBackToDocuments}
                  onContinueToEvidence={handleContinueToEvidence}
                  isExtractionLoading={isExtractionLoading}
                />
              ) : shouldShowDraftingFlow ? (
                <ReportDraftingFlow
                  report={report}
                  isEmbedded
                  onBack={handleBackFromDrafting}
                  onContinueToPreview={handleContinueToPreview}
                  onAutosaveActivity={handleDraftAutosaveActivity}
                  onRegisterPersistenceControls={(controls) => {
                    draftPersistenceControlsRef.current = controls || null;
                  }}
                  toolbarPortalTarget={draftToolbarHostElement}
                />
              ) : shouldShowPreviewFlow ? (
                <ReportPreviewFlow
                  report={report}
                  isEmbedded
                  onBack={handleBackToDrafting}
                  onOpenExport={handleOpenExport}
                  onCloseExport={handleCloseExport}
                  onExportComplete={handleExportComplete}
                  isExportStep={isExportStep}
                />
              ) : (
                <ModuleShell
                  report={report}
                  activeModule={activeModule}
                  moduleList={reportModules}
                  moduleBlocks={blocksByModule[activeModule] || []}
                  onUpdateBlocks={updateModuleBlocks}
                  currentUser={user}
                  onBackToExtraction={handleBackToExtraction}
                  onPreviousModule={handlePreviousModule}
                  onNextModule={handleNextModule}
                  onContinueToAssembled={handleContinueToAssembled}
                  onSaveModule={handleSaveModule}
                  onLockModule={handleLockModule}
                  onStatusTransition={handleStatusTransition}
                  onCompleteModule={handleCompleteModule}
                  onCompleteAllModules={handleCompleteAllModules}
                  onApproveAllBlocks={handleApproveAllBlocks}
                  canProceedToNextModule={isActiveModuleApproved}
                  canProceedToDrafting={areAllModulesApproved}
                />
              )}
            </div>
          </div>
        </div>

        {shouldShowWorkspacePanels && dossier && (
          <DossierPanel
            dossier={dossier}
            reportType="plants"
            className="report-workspace-page__dossier"
          />
        )}
      </div>

      <DocumentsDrawer
        isOpen={isDocumentsDrawerOpen}
        onClose={() => setIsDocumentsDrawerOpen(false)}
        documents={documents}
        onUpload={handleDocumentsUpload}
        currentUser={user}
        activeStep={activeStepperStep}
      />

      <CommentsDrawer
        isOpen={isCommentsDrawerOpen}
        onClose={() => setIsCommentsDrawerOpen(false)}
        comments={comments}
        onAddComment={handleAddComment}
        onReply={handleReply}
        onEdit={handleEditComment}
        onDelete={handleDeleteComment}
        onResolve={handleResolveComment}
        currentUser={user}
      />

      <ActivityLogDrawer
        isOpen={isActivityLogDrawerOpen}
        onClose={() => setIsActivityLogDrawerOpen(false)}
        activities={activities}
      />
    </div>
  );
}

export default ReportWorkspacePage;
